import React, { useEffect, useRef, useState } from "react";

import { format, isValid, parse, endOfDay } from "date-fns";
import FocusTrap from "focus-trap-react";
import {
  CaptionLabel,
  DayPicker,
  useDayPicker,
  useNavigation,
} from "react-day-picker";
import { usePopper } from "react-popper";
import { Input } from "reactstrap";
import "./styles.scss";

import CalendarIcon from "../../images/icons/calendar.svg";
import { MonthsDropdown } from "./MonthsDropdown";
import { YearsDropdown } from "./YearsDropdown";
import { CaptionNavigation } from "./CaptionNavigation";

const CustomCaption = (props) => {
  const { goToMonth } = useNavigation();
  const { classNames, styles, components } = useDayPicker();

  const handleMonthChange = (newMonth) => {
    goToMonth(newMonth);
  };
  const CaptionLabelComponent = components?.CaptionLabel ?? CaptionLabel;
  const captionLabel = (
    <CaptionLabelComponent id={props.id} displayMonth={props.displayMonth} />
  );
  return (
    <div className="rdp-caption">
      <div
        className={classNames.caption_dropdowns}
        style={styles.caption_dropdowns}
      >
        {/* Caption label is visually hidden but for a11y. */}
        <div className={classNames.vhidden}>{captionLabel}</div>
        <MonthsDropdown
          onChange={handleMonthChange}
          displayMonth={props.displayMonth}
        />
        <YearsDropdown
          onChange={handleMonthChange}
          displayMonth={props.displayMonth}
        />
      </div>
      <div>
        <CaptionNavigation displayMonth={props.displayMonth} id={props.id} />
      </div>
    </div>
  );
};

const dateFormat = "MM/dd/yyyy";

const DatePickerInput = ({
  selected,
  setSelected,
  fromDate,
  toDate,
  isEndOfDay,
}) => {
  const [inputValue, setInputValue] = useState("");
  const [isPopperOpen, setIsPopperOpen] = useState(false);

  const popperRef = useRef(null);
  const buttonRef = useRef(null);
  const [popperElement, setPopperElement] = useState(null);

  const popper = usePopper(popperRef.current, popperElement, {
    placement: "bottom-start",
  });

  useEffect(() => {
    if (selected) {
      setInputValue(format(selected, dateFormat));
      closePopper();
    } else {
      setInputValue("");
    }
  }, [selected]);

  const closePopper = (e) => {
    setIsPopperOpen(false);
    buttonRef?.current?.focus();
  };

  const handleInputChange = (e) => {
    setInputValue(e.currentTarget.value);
    let date = parse(e.currentTarget.value, dateFormat, new Date());
    if (isEndOfDay) {
      date = endOfDay(date);
    }
    if (isValid(date)) {
      setSelected(date);
    } else {
      setSelected(undefined);
    }
  };

  const handleButtonClick = () => {
    setIsPopperOpen((prev) => !prev);
  };

  const handleDaySelect = (date) => {
    if (isEndOfDay) {
      date = endOfDay(date);
    }
    setSelected(date);
  };

  return (
    <div>
      <div className="input-container" ref={popperRef}>
        <Input type="text" value={inputValue} onChange={handleInputChange} />
        <button
          ref={buttonRef}
          type="button"
          aria-label="Pick a date"
          onClick={handleButtonClick}
        >
          <img src={CalendarIcon} aria-label="calendar icon"></img>
        </button>
      </div>
      {isPopperOpen && (
        <FocusTrap
          active
          focusTrapOptions={{
            initialFocus: false,
            allowOutsideClick: true,
            clickOutsideDeactivates: (e) => {
              return !(
                e.target === buttonRef.current ||
                e.target === buttonRef.current.lastChild
              );
            },
            onDeactivate: closePopper,
            fallbackFocus: buttonRef.current,
          }}
        >
          <div
            tabIndex={-1}
            style={popper.styles.popper}
            className="dialog-sheet"
            {...popper.attributes.popper}
            ref={setPopperElement}
            role="dialog"
          >
            <DayPicker
              initialFocus={isPopperOpen}
              mode="single"
              defaultMonth={selected}
              selected={selected}
              onSelect={handleDaySelect}
              fixedWeeks
              fromDate={fromDate}
              toDate={toDate}
              components={{
                Caption: CustomCaption,
              }}
            />
          </div>
        </FocusTrap>
      )}
    </div>
  );
};

export default DatePickerInput;
